import {
  GetStopStatusMyRedeliveryStop,
  GetStopStatusMyStop,
  GetStopStatusOrder,
} from 'src/graphql/types/delivery';
import { StopStatusState } from 'src/state/slices/delivery';
import { isMultiDayDeliveryWindow } from 'src/utils/date';

// The specialty-icon flags shown on a stop (bien cuit, pastry, other vendors, refrigerated dairy,
// refrigeration / freezer). Derived purely from a stop's orders so both the /deliveries stop status
// (Redux thunk) and the scenario route list compute the identical set — the single source of truth
// for "which icons does this stop show".
export type StopIconFlags = {
  isRefrigerationRequired: boolean;
  isFreezerRequired: boolean;
  hasRefrigeratedDairy: boolean;
  hasBienCuitOrder: boolean;
  hasPastryOrder: boolean;
  hasTrackedVendorOrder: boolean;
};

// Structural shape covering both the /deliveries Order type and the scenario stop's orders — only the
// fields the icon logic reads, so any query selecting them satisfies it.
export type StopIconOrder = {
  itemLevelTracking?: boolean | null;
  vendor?: { legalName?: string | null; isWarehouse?: boolean | null } | null;
  orderLines?: ReadonlyArray<{
    sku?: {
      refrigerationState?: string | null;
      subcategoryName?: string | null;
    } | null;
  } | null> | null;
  scheduledDeliveryTime?: {
    earliest: string;
    latest: string;
    timeZone: string;
  } | null;
};

const someOrderLine = (
  order: StopIconOrder,
  predicate: (sku: {
    refrigerationState?: string | null;
    subcategoryName?: string | null;
  }) => boolean,
): boolean =>
  order.orderLines?.some((line) => (line?.sku ? predicate(line.sku) : false)) ??
  false;

export const deriveStopIconFlags = (
  orders: ReadonlyArray<StopIconOrder> | null | undefined,
): StopIconFlags => {
  const list = orders ?? [];
  return {
    isRefrigerationRequired: list.some((order) =>
      someOrderLine(order, (sku) => sku.refrigerationState === 'refrigerated'),
    ),
    isFreezerRequired: list.some((order) =>
      someOrderLine(order, (sku) => sku.refrigerationState === 'frozen_state'),
    ),
    hasRefrigeratedDairy: list.some((order) =>
      someOrderLine(
        order,
        (sku) =>
          sku.refrigerationState === 'refrigerated' &&
          sku.subcategoryName === 'Dairy',
      ),
    ),
    hasBienCuitOrder: list.some(
      (order) => order.vendor?.legalName === 'Bien Cuit',
    ),
    // Non-item-level-tracked vendors other than Bien Cuit surface as generic pastry orders.
    hasPastryOrder: list.some(
      (order) =>
        order.vendor?.legalName !== 'Bien Cuit' && !order.itemLevelTracking,
    ),
    // Item-level-tracked, non-warehouse vendors (besides Bien Cuit) fall out of the pastry indicator.
    hasTrackedVendorOrder: list.some(
      (order) =>
        order.vendor?.legalName !== 'Bien Cuit' &&
        !order.vendor?.isWarehouse &&
        !!order.itemLevelTracking,
    ),
  };
};

// A stop's delivery-window kind for the day/night icon: nighttime when the scheduled window spans two
// calendar days (matches DeliveryWindow.tsx), daytime otherwise, undefined when no order carries a
// scheduled time (no icon — same as the /deliveries card).
export type StopWindow = 'day' | 'night';

export const deriveStopWindow = (
  orders: ReadonlyArray<StopIconOrder> | null | undefined,
): StopWindow | undefined => {
  const scheduled = (orders ?? [])
    .map((order) => order.scheduledDeliveryTime)
    .find((time): time is NonNullable<typeof time> => Boolean(time));
  if (!scheduled) return undefined;
  return isMultiDayDeliveryWindow(scheduled) ? 'night' : 'day';
};

export const foundIssues = (
  stop: GetStopStatusMyStop | GetStopStatusMyRedeliveryStop,
): boolean => {
  const orderIssue = (stop.orders as GetStopStatusOrder[])?.find(
    (order: GetStopStatusOrder) => (order.issues?.length ?? 0) > 0,
  );
  return orderIssue !== undefined || !!stop.issues?.length;
};

export const foundNotes = (
  stop: GetStopStatusMyStop | GetStopStatusMyRedeliveryStop,
): boolean => {
  const issueWithDriverNote = stop.issues?.find((issue) => {
    return !!issue.note;
  });
  const orderIssuesWithNote = (stop.orders as GetStopStatusOrder[])?.find(
    (order: GetStopStatusOrder) =>
      order.issues?.find((orderIssue) => !!orderIssue.note),
  );
  return orderIssuesWithNote !== undefined || issueWithDriverNote !== undefined;
};

export const foundDeliveryNote = (
  stop: GetStopStatusMyStop | GetStopStatusMyRedeliveryStop,
): boolean => {
  const deliveryNote =
    stop.__typename === 'Delivery' ? stop.deliveryNote : null;
  return !!deliveryNote;
};

export const doesStopHaveInfo = (stopStatuses: StopStatusState[]): boolean => {
  return stopStatuses.some((myStopStatus) => {
    if (!myStopStatus) return false;
    return Object.entries(myStopStatus.stopSecondaryStatus).some(
      ([key, value]) => {
        // we don't want to factor refrigeration state into this status
        if (
          key === 'isRefrigerationRequired' ||
          key === 'isFreezerRequired' ||
          key === 'hasPastryOrder' ||
          key === 'hasTrackedVendorOrder' ||
          key === 'hasBienCuitOrder'
        )
          return false;
        return value;
      },
    );
  });
};
